<template>
  <DossierDocumentCard
    title="Responsável Técnico"
    :status-options="statusOptions"
    :multiple-status="true"
    :status-requires-validity="['possui_vigente', 'possui_provisorio']"
    :status-requires-upload="['possui_vigente', 'possui_provisorio']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="false"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/technical-manager"
    upload-endpoint="/rest-home/dossier/technical-manager/upload"
    @saved="onSaved"
    @error="onError"
  >
    <!-- ─── Campos específicos do RT (acima do status) ─── -->
    <template #fields-before>
      <div class="dossier-rt__section">
        <!-- ── Bloco de busca por CPF ── -->
        <div class="dossier-rt__search-block">
          <p class="dossier-rt__section-label">
            <i class="fas fa-user-md mr-1"></i>
            Identificação do Responsável Técnico
          </p>

          <!-- Linha: CPF + botão buscar -->
          <div class="dossier-rt__cpf-row">
            <div class="dossier-rt__field dossier-rt__field--cpf">
              <label class="dossier-rt__label dossier-rt__label--required">
                CPF
              </label>

              <!-- Modo edição -->
              <v-text-field
                v-if="!isViewMode"
                v-model="formData.cpf"
                v-mask="'###.###.###-##'"
                dense
                outlined
                hide-details="auto"
                placeholder="000.000.000-00"
                :rules="[rules.required, rules.cpf]"
                :disabled="cpfLocked"
                class="dossier-rt__input"
                @blur="onCpfBlur"
              />

              <!-- Modo visualização -->
              <span v-else class="dossier-rt__view-value">
                {{ formData.cpf || "—" }}
              </span>
            </div>

            <!-- Botão buscar (só no modo edição e quando CPF não está travado) -->
            <div
              v-if="!isViewMode"
              class="dossier-rt__field dossier-rt__field--action"
            >
              <label class="dossier-rt__label">&nbsp;</label>
              <v-btn
                v-if="!cpfLocked"
                small
                class="btn-add"
                :loading="searchLoading"
                :disabled="!isCpfComplete"
                @click="searchByCpf"
              >
                <i class="fas fa-search mr-1"></i>
                Buscar
              </v-btn>

              <!-- Botão para destravar e trocar o RT -->
              <v-btn v-else small class="btn-notcertified" @click="unlockCpf">
                <i class="fas fa-exchange-alt mr-1"></i>
                Trocar RT
              </v-btn>
            </div>
          </div>

          <!-- Feedback de busca -->
          <div v-if="searchError" class="dossier-rt__search-error">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ searchError }}
          </div>

          <!-- Aviso: profissional não encontrado no sistema -->
          <div v-if="professionalNotFound" class="dossier-rt__not-found">
            <i class="fas fa-info-circle mr-1"></i>
            Profissional não encontrado no sistema.
            <a
              v-if="!isViewMode"
              class="dossier-rt__link"
              @click="fillManually"
            >
              Preencher manualmente
            </a>
          </div>
        </div>

        <!-- ── Dados do profissional (preenchidos após busca ou manualmente) ── -->
        <div
          v-if="showProfessionalFields"
          class="dossier-rt__professional-fields"
          :class="{ 'dossier-rt__professional-fields--auto': autoFilled }"
        >
          <!-- Aviso de dados automáticos -->
          <div v-if="autoFilled && !isViewMode" class="dossier-rt__auto-badge">
            <i class="fas fa-sync-alt mr-1"></i>
            Dados preenchidos automaticamente do cadastro do usuário
          </div>

          <div class="dossier-rt__fields-grid">
            <!-- Nome completo -->
            <div class="dossier-rt__field dossier-rt__field--full">
              <label class="dossier-rt__label dossier-rt__label--required">
                Nome completo
              </label>
              <v-text-field
                v-if="!isViewMode && !autoFilled"
                v-model="formData.rt_name"
                dense
                outlined
                hide-details="auto"
                :rules="[rules.required]"
                class="dossier-rt__input"
              />
              <span
                v-else
                class="dossier-rt__view-value dossier-rt__view-value--highlight"
              >
                {{ formData.rt_name || "—" }}
              </span>
            </div>

            <!-- Profissão / Cargo -->
            <div class="dossier-rt__field">
              <label class="dossier-rt__label">Profissão / Cargo</label>
              <v-text-field
                v-if="!isViewMode && !autoFilled"
                v-model="formData.rt_profession"
                dense
                outlined
                hide-details
                class="dossier-rt__input"
              />
              <span v-else class="dossier-rt__view-value">
                {{ formData.rt_profession || "—" }}
              </span>
            </div>

            <!-- Conselho de classe -->
            <div class="dossier-rt__field">
              <label class="dossier-rt__label">Conselho de classe</label>
              <v-text-field
                v-if="!isViewMode && !autoFilled"
                v-model="formData.rt_council"
                dense
                outlined
                hide-details
                placeholder="Ex: CRM, COREN, CRN"
                class="dossier-rt__input"
              />
              <span v-else class="dossier-rt__view-value">
                {{ formData.rt_council || "—" }}
              </span>
            </div>

            <!-- Número do registro -->
            <div class="dossier-rt__field">
              <label class="dossier-rt__label">Número do registro</label>
              <v-text-field
                v-if="!isViewMode && !autoFilled"
                v-model="formData.rt_council_number"
                dense
                outlined
                hide-details
                class="dossier-rt__input"
              />
              <span v-else class="dossier-rt__view-value">
                {{ formData.rt_council_number || "—" }}
              </span>
            </div>

            <!-- UF do registro -->
            <div class="dossier-rt__field dossier-rt__field--small">
              <label class="dossier-rt__label">UF do registro</label>
              <v-text-field
                v-if="!isViewMode && !autoFilled"
                v-model="formData.rt_council_uf"
                dense
                outlined
                hide-details
                maxlength="2"
                placeholder="SP"
                class="dossier-rt__input"
              />
              <span v-else class="dossier-rt__view-value">
                {{ formData.rt_council_uf || "—" }}
              </span>
            </div>

            <!-- Data de início da responsabilidade técnica -->
            <div class="dossier-rt__field">
              <label class="dossier-rt__label dossier-rt__label--required">
                Início da responsabilidade técnica
              </label>
              <v-text-field
                v-if="!isViewMode"
                v-model="formData.rt_start_date"
                type="date"
                dense
                outlined
                hide-details="auto"
                :rules="[rules.required]"
                class="dossier-rt__input"
              />
              <span v-else class="dossier-rt__view-value">
                <i class="fas fa-calendar-alt mr-1"></i>
                {{ formatDateBR(formData.rt_start_date) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Separador visual antes do bloco de status -->
        <div v-if="showProfessionalFields" class="dossier-rt__divider"></div>
      </div>
    </template>

    <!-- ─── Campos específicos após o status (Termo RT) ─── -->
    <template #fields-after>
      <!-- Regra de atualização do Termo -->
      <div v-if="!isViewMode" class="dossier-rt__update-rule">
        <i class="fas fa-info-circle mr-1"></i>
        Sempre que houver troca de RT, alteração de função ou atualização do
        documento, o termo deve ser substituído.
      </div>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";
import api from "../../services/api";

const EXPLAINER_TEXT = `
  O Termo de Responsabilidade Técnica formaliza quem responde tecnicamente
  pela ILPI, definindo as atribuições e os limites de atuação do Responsável
  Técnico, conforme a organização da própria instituição.<br><br>
  O termo deve ser elaborado pela ILPI e conter: identificação da instituição,
  do Responsável Técnico, descrição da função exercida e período de vigência.<br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua
  existência para fins de organização e apresentação à fiscalização.
`;

export default {
  name: "DossierTechnicalManager",

  components: { DossierDocumentCard },

  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      explainerText: EXPLAINER_TEXT,

      statusOptions: [
        { value: "possui_vigente", label: "Possui termo vigente" },
        { value: "possui_provisorio", label: "Possui termo provisório" },
        { value: "em_elaboracao", label: "Possui documento em elaboração" },
        { value: "nao_possui", label: "Não possui termo (pendente)" },
      ],

      // Estado do formulário
      formData: {
        // Dados do RT
        cpf: "",
        rt_name: "",
        rt_profession: "",
        rt_council: "",
        rt_council_number: "",
        rt_council_uf: "",
        rt_start_date: null,
        // Dados do Termo (gerenciados pelo DossierDocumentCard via v-model)
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
        // ID interno do usuário encontrado (para vinculação no backend)
        user_id: null,
        ...this.flattenInitialData(),
      },

      // Estado da busca por CPF
      searchLoading: false,
      searchError: "",
      professionalNotFound: false,

      // true = dados vieram do cadastro do sistema (bloqueados para edição)
      autoFilled: false,
      // true = CPF foi confirmado e está travado (não pode mudar sem clicar Trocar RT)
      cpfLocked: false,
      // true = exibe o bloco de dados do profissional
      showProfessionalFields: false,

      rules: {
        required: (v) => !!v || "Campo obrigatório",
        cpf: (v) => {
          if (!v) return true;
          return v.replace(/\D/g, "").length === 11 || "CPF inválido";
        },
      },
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    // CPF completo = 14 chars com máscara (000.000.000-00)
    isCpfComplete() {
      return (this.formData.cpf || "").replace(/\D/g, "").length === 11;
    },
  },

  watch: {
    initialData: {
      deep: true,
      handler() {
        const d = this.flattenInitialData();
        this.formData = { ...this.formData, ...d };

        // Se já tem dados do RT, exibe o bloco e marca como travado
        if (d.cpf) {
          this.cpfLocked = true;
          this.showProfessionalFields = true;
          // Se veio com user_id, é dado automático do cadastro
          this.autoFilled = !!d.user_id;
        }
      },
    },
  },

  created() {
    // Se já há dados iniciais ao montar, inicializa o estado de exibição
    const d = this.initialData || {};
    if (d.cpf) {
      this.cpfLocked = true;
      this.showProfessionalFields = true;
      this.autoFilled = !!d.user_id;
    }
  },

  methods: {
    // ── Busca pelo CPF ─────────────────────────────────────────────────────
    async searchByCpf() {
      const cpf = (this.formData.cpf || "").replace(/\D/g, "");
      if (cpf.length !== 11) return;

      this.searchLoading = true;
      this.searchError = "";
      this.professionalNotFound = false;

      try {
        const response = await api.get("/rest-home/user/find-by-cpf", {
          params: { cpf },
        });

        const { error, user } = response.data;

        if (error || !user) {
          // CPF não encontrado no sistema → permite preenchimento manual
          this.professionalNotFound = true;
          this.autoFilled = false;
          this.showProfessionalFields = true;
          this.cpfLocked = true;
          return;
        }

        // Preenche automaticamente com os dados do cadastro
        this.fillFromUser(user);
      } catch (err) {
        this.searchError = "Erro ao buscar profissional. Tente novamente.";
        console.error("DossierTechnicalManager.searchByCpf:", err);
      } finally {
        this.searchLoading = false;
      }
    },

    // Dispara a busca ao sair do campo CPF (se estiver completo)
    onCpfBlur() {
      if (this.isCpfComplete && !this.cpfLocked) {
        this.searchByCpf();
      }
    },

    // ── Preenche com dados do usuário encontrado ───────────────────────────
    fillFromUser(user) {
      // Extrai conselho e número do valor composto (ex: "CRM-SP 12345")
      const { council, number, uf } = this.parseCouncilValue(
        user.professional_class_council_value || ""
      );

      this.formData.user_id = user.id;
      this.formData.rt_name = user.name || "";
      this.formData.rt_profession = user.office || "";
      this.formData.rt_council = council;
      this.formData.rt_council_number = number;
      this.formData.rt_council_uf = uf;

      // Mantém a data de início se já estava preenchida
      if (!this.formData.rt_start_date) {
        this.formData.rt_start_date = null;
      }

      this.autoFilled = true;
      this.cpfLocked = true;
      this.showProfessionalFields = true;
      this.professionalNotFound = false;
    },

    // ── Destravar CPF para troca de RT ────────────────────────────────────
    unlockCpf() {
      this.cpfLocked = false;
      this.autoFilled = false;
      this.showProfessionalFields = false;
      this.professionalNotFound = false;
      this.searchError = "";

      this.formData.cpf = "";
      this.formData.user_id = null;
      this.formData.rt_name = "";
      this.formData.rt_profession = "";
      this.formData.rt_council = "";
      this.formData.rt_council_number = "";
      this.formData.rt_council_uf = "";
      this.formData.rt_start_date = null;
    },

    // ── Preencher manualmente (quando não encontrou no sistema) ───────────
    fillManually() {
      this.professionalNotFound = false;
      this.autoFilled = false;
      this.showProfessionalFields = true;
    },

    // ── Callbacks do DossierDocumentCard ──────────────────────────────────
    onSaved(savedData) {
      this.$emit("saved", { module: "technical_manager", data: savedData });
    },

    onError(errors) {
      this.$emit("error", { module: "technical_manager", errors });
    },

    // ── Utilitários ────────────────────────────────────────────────────────

    // Converte "CRM-SP 12345" → { council: 'CRM', uf: 'SP', number: '12345' }
    parseCouncilValue(value) {
      if (!value) return { council: "", number: "", uf: "" };
      try {
        // Padrão COREN: "COREN-SP-12345-ENF"
        if (value.startsWith("COREN")) {
          const parts = value.split("-");
          return {
            council: "COREN",
            uf: parts[1] || "",
            number: parts[2] || "",
          };
        }
        // Padrão outros: "CRM-SP 12345"
        const dashParts = value.split("-");
        const council = dashParts[0] || "";
        const rest = (dashParts[1] || "").trim().split(" ");
        return {
          council,
          uf: rest[0] || "",
          number: rest[1] || "",
        };
      } catch {
        return { council: value, number: "", uf: "" };
      }
    },

    flattenInitialData() {
      const d = this.initialData || {};
      return {
        cpf: d.cpf || "",
        rt_name: d.rt_name || "",
        rt_profession: d.rt_profession || "",
        rt_council: d.rt_council || "",
        rt_council_number: d.rt_council_number || "",
        rt_council_uf: d.rt_council_uf || "",
        rt_start_date: d.rt_start_date || null,
        user_id: d.user_id || null,
        status: d.status || [],
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },

    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER RESPONSÁVEL TÉCNICO
   Estilos dos blocos específicos deste módulo.
   ═══════════════════════════════════════════════════════════════ */

/* ── Seção principal ─────────────────────────────────────────── */
.dossier-rt__section {
  margin-bottom: 4px;
}

.dossier-rt__section-label {
  font-size: 12px;
  font-weight: 600;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px;
}

/* ── Bloco de busca ──────────────────────────────────────────── */
.dossier-rt__search-block {
  margin-bottom: 12px;
}

.dossier-rt__cpf-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.dossier-rt__field {
  display: flex;
  flex-direction: column;
}

.dossier-rt__field--cpf {
  flex: 0 0 220px;
}

.dossier-rt__field--action {
  flex: 0 0 auto;
}

.dossier-rt__field--full {
  flex: 0 0 100%;
}

.dossier-rt__field--small {
  flex: 0 0 100px;
}

/* ── Labels ──────────────────────────────────────────────────── */
.dossier-rt__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}

.dossier-rt__label--required::after {
  content: " *";
  color: #dc3545;
}

/* ── Input ───────────────────────────────────────────────────── */
.dossier-rt__input {
  font-size: 13px;
}

/* ── Feedbacks de busca ──────────────────────────────────────── */
.dossier-rt__search-error {
  margin-top: 6px;
  font-size: 12px;
  color: #dc3545;
  font-weight: 600;
}

.dossier-rt__not-found {
  margin-top: 8px;
  font-size: 13px;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 8px 10px;
}

.dossier-rt__link {
  color: #1f5a76;
  font-weight: 600;
  cursor: pointer;
  margin-left: 4px;
  text-decoration: underline;
}

/* ── Bloco de dados do profissional ──────────────────────────── */
.dossier-rt__professional-fields {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
}

/* Quando dados são automáticos: borda azul discreta */
.dossier-rt__professional-fields--auto {
  border-color: #b8d9ec;
  background: #f4fafd;
}

.dossier-rt__auto-badge {
  font-size: 11px;
  color: #1f5a76;
  font-weight: 600;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #b8d9ec;
}

.dossier-rt__fields-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.dossier-rt__fields-grid .dossier-rt__field {
  flex: 1;
  min-width: 180px;
}

.dossier-rt__fields-grid .dossier-rt__field--full {
  flex: 0 0 100%;
}

.dossier-rt__fields-grid .dossier-rt__field--small {
  flex: 0 0 90px;
  min-width: 90px;
}

/* ── Valores em modo visualização ────────────────────────────── */
.dossier-rt__view-value {
  font-size: 13px;
  color: #333;
  padding: 6px 0;
  display: block;
}

.dossier-rt__view-value--highlight {
  font-weight: 600;
  font-size: 14px;
  color: #1f5a76;
}

/* ── Separador ───────────────────────────────────────────────── */
.dossier-rt__divider {
  height: 1px;
  background: #e8e8e8;
  margin: 4px 0 14px;
}

/* ── Regra de atualização ────────────────────────────────────── */
.dossier-rt__update-rule {
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin-bottom: 12px;
  padding: 8px 10px;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-rt__cpf-row {
    flex-direction: column;
    align-items: stretch;
  }

  .dossier-rt__field--cpf {
    flex: unset;
    width: 100%;
  }

  .dossier-rt__fields-grid .dossier-rt__field {
    flex: 0 0 100%;
  }

  .dossier-rt__field--small {
    flex: 0 0 100%;
  }
}
</style>